import React, { useMemo, useRef, useState } from "react";
import BlueprintModeGrid from "./BlueprintModeGrid";
import { buildConnectorGraph } from "./connectorsGraph";
import {
  buildYearOneGroups,
  buildRoster,
  countShortlisted,
  initialsFor,
} from "./yearOnePlan";
// The Year 1 tab's sections, shared with both live screens (2026-09-03). It
// brings `YearOnePlan.scss` with it, which is where the `yop-*` classes used in
// that tab below come from.
import YearOnePlanSections from "./YearOnePlanSections";
// The shared skeleton chip — one shimmer across every live-review screen.
import EbSkeleton from "./EbSkeleton";
// ⚠️ THE ONE APP-LEVEL COMPONENT THIS FILE IMPORTS, and it is safe for the
// byte-identity rule for a specific reason: the relative path resolves to the
// same place in both repos. `_shared/` → `../../../../` is `src/components/` in
// each app, because the two trees are `components/CoachPanel/…` and
// `components/UserPanel/…` — the same depth.
//
// It was SME-only until 2026-09-03; it is now copied into the coach repo at the
// identical path, so both resolve. Keep the two copies in step, and do NOT
// substitute the coach app's `StandardTooltip` here — the instruction is that
// this is the product's standard tooltip.
import { ElegantTooltip } from "../../../../shared/formComponents/ElegantTooltip/ElegantTooltip";
import "./LockedBlueprintView.scss";

// ═════════════════════════════════════════════════════════════════════════════
// The locked, read-only view — Figma node 33869:43260.
//
// ⚠️ THIS FILE IS INTENTIONALLY IDENTICAL IN TWO REPOSITORIES.
//
//   10x-coach-frontend/src/components/CoachPanel/ExecutionBlueprint/
//     ExecutionBlueprintLiveReview/_shared/LockedBlueprintView.js
//   10x-frontend/src/components/UserPanel/ExecutionBlueprint/
//     ExecutionBlueprintLiveReview/_shared/LockedBlueprintView.js
//
// `diff` is the test; change one copy, change both in the same pass.
//
// ── ONE SCREEN, THREE TABS — and now the ONLY locked screen ─────────────────
// The design draws Blueprint / WIG Connections / Year 1 Plan as tabs across a
// single frame. The tab lives in the URL: the breadcrumb in the design reads
// "Execution Blueprint › WIG Connections", so the tab is part of where you are,
// not transient state — and a locked blueprint is exactly the kind of thing
// someone links a colleague to.
//
// ⚠️ There used to be a HUB in front of this screen — a "Final Locked
// Blueprint" landing page whose three cards each opened this view on the
// matching tab. It was DELETED ENTIRELY (2026-09-02, on instruction): everything
// that used to land on it now lands here, on the `blueprint` tab. The tabs were
// always the real navigation, so the hub was a click in front of them offering
// the same three destinations under different art.
//
// ── Everything here is already built ────────────────────────────────────────
// This composes rather than draws: `BlueprintModeGrid` for the wall chart,
// `buildConnectorGraph` + the canvas for the map, `buildYearOneGroups` for the
// shortlist. All three read the same normalised record, so the locked view is
// the same data with every control removed.
//
// ── Download PDF ────────────────────────────────────────────────────────────
// The design's top-right button. Absent for most of this build (§9: "no PDF
// export... don't promise a download button"), then built client-side, and now
// SERVER-GENERATED — which is what §9 was describing all along.
//
// ⚠️ ONE BUTTON, ONE FILE, ALL THREE TABS (2026-09-03). It used to export the
// ACTIVE tab only, on instruction at the time ("every thing will be seprate"),
// by photographing this component's own DOM with html2canvas + jsPDF. That is
// gone: `lockedBlueprintPdf.js` is deleted and the endpoint returns the whole
// blueprint as one document.
//
// Worth recording why the client-side version was the wrong shape, not just
// superseded. It photographed a live DOM, so it inherited every quirk of that
// DOM and needed three rounds of fixes for things a server renderer never has:
// a wall chart cropped to its scroll container, band rails made unreadable by
// `writing-mode` (which html2canvas does not implement), and a connector layer
// scaled twice because the zoom transform was applied to both the SVG and its
// container. None of those were bugs in the export logic — they were the cost
// of screenshotting a screen.
//
// The canvas is passed `readOnly`, which removes the affordances rather than
// just the handlers — see that prop's comment for why that distinction matters.
// ═════════════════════════════════════════════════════════════════════════════

// The three stages, in the order the journey ran. `tab` is the URL segment the
// view screen routes on.
//
// ⚠️ This LIVED IN `LockedBlueprintHub.js` until that file was deleted
// (2026-09-02). It sits here now because this file already owns the labels and
// `isLockedTab`, and because its only other consumer — `LockedBlueprintScreen`
// — already imports `isLockedTab` from here, so the move cost that file nothing
// but a longer import line. Placement in the hub was never a decision; it was
// where the hub happened to be built first.
export const LOCKED_TABS = {
  BLUEPRINT: "blueprint",
  CONNECTIONS: "connections",
  YEAR_ONE: "year-one",
};

// Tab labels are the short forms of JK's names (UAT #83, #85); the screen
// title and breadcrumb carry the full ones ("Execution Blueprint Diagram with
// Connectors", "Prioritised WIGs for the next 12 months").
const TABS = [
  { key: LOCKED_TABS.BLUEPRINT, label: "Blueprint" },
  { key: LOCKED_TABS.CONNECTIONS, label: "Diagram with Connectors" },
  { key: LOCKED_TABS.YEAR_ONE, label: "Prioritised WIGs" },
];

export const isLockedTab = (tab) => TABS.some((t) => t.key === tab);

// ── Zoom, on the read-only map ───────────────────────────────────────────────
// Added 2026-08-31 on instruction: the locked view had none, so a map wider than
// the viewport could only be read by scrolling. That is worse here than on the
// live screen — nothing can be moved or collapsed to make room, so zooming out
// is the ONLY way to see the shape of the thing, which is the main reason to
// open a locked blueprint at all.
//
// Same bounds and step as the live screen (`WigConnections.js`), deliberately:
// 100% is the map's designed size and magnifying past it only pushes connected
// WIGs off screen, while 60% is where the labels stop being readable. Kept in
// step by hand — this file cannot import them, being shared byte-identically
// across two repos.
const ZOOM_MIN = 0.6;
const ZOOM_MAX = 1;
const ZOOM_STEP = 0.1;

// How many owner avatars a locked row shows before the rest become "+N". Four
// fits the widened owner column; beyond that the stack starts eating the WIG
// text beside it.
const LOCKED_AVATAR_LIMIT = 4;

// The locked badge's padlock. Inlined for the same reason as `DownloadGlyph`
// below.
const LockGlyph = ({ size = 11 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
  >
    <rect
      x="3"
      y="7"
      width="10"
      height="7"
      rx="1.6"
      stroke="currentColor"
      strokeWidth="1.7"
    />
    <path
      d="M5.5 7V5.2a2.5 2.5 0 0 1 5 0V7"
      stroke="currentColor"
      strokeWidth="1.7"
    />
  </svg>
);

// Inlined rather than imported, for the reason in the header: the coach repo
// has an icon set to borrow from and the CEO repo does not, so any import here
// would resolve differently in the two apps.
const DownloadGlyph = ({ size = 14 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M8 2v8m0 0L5 7m3 3 3-3"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M2.5 11.5v1A1.5 1.5 0 0 0 4 14h8a1.5 1.5 0 0 0 1.5-1.5v-1"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

// (`CheckGlyph` lived here — the tick inside the Year 1 tab's "Shortlisted"
// tag. That tag went on 2026-09-03 (every row on this tab is shortlisted, so
// the mark distinguished nothing), leaving this its only consumer. Deleted
// rather than kept "in case": the live screens draw their own tick from their
// own app's `CheckIcon`, so nothing else here ever needed it.)

// (`OverviewStat` lived here. Its only consumer was the Connection Overview
// panel, removed 2026-08-31 — see the note on the connections tab. Deleted
// rather than left behind: a styled component nothing renders is an invitation
// to bring the panel back by accident. `.lbv-stat*` rules are likewise now
// unused in the stylesheet.)

// ── Loading ─────────────────────────────────────────────────────────────────
// A skeleton of the view, NOT "Loading the locked blueprint…" — replaced on
// instruction (2026-08-31), same call as the hub's.
//
// Mirrors the parts of this screen that are CONSTANT: the 1fr/auto/1fr bar with
// its three tab pills and the 36px download button, then one body block. The
// body deliberately gets a single large placeholder rather than a fake graph or
// fake table, because what goes there depends on the tab and inventing three
// different fake shapes would be guessing at content — a plain block is honest
// about "something is coming" without pretending to know what.
// ⚠️ THE BODY IS NO LONGER ONE GREY SLAB (2026-09-03). It was a single
// `.lbv-skel-body` filling the whole panel, which is the exact thing CLAUDE.md's
// skeleton rule exists to prevent — "skeletons mirror real layout, not a
// spinner". A full-height rectangle tells the reader nothing about what is
// coming and then rearranges the entire screen when it arrives.
//
// It now mirrors the tab this screen opens on. `blueprint` is the default tab
// (the hub in front of it was deleted 2026-09-02, so every entry point lands
// here), and that tab is `BlueprintModeGrid` — a BHAG bar across the top and
// four WIG columns beneath. Those four columns are structural, not data: every
// blueprint has Financial, Customer, Operations and People, so the skeleton can
// promise them honestly before anything loads.
//
// The three tab pills and the download button above are unchanged; they were
// already right.
export const LockedBlueprintViewSkeleton = () => (
  <div className="lbv" aria-hidden="true">
    <div className="lbv-bar">
      <div className="lbv-leading" />
      <div className="lbv-tabs">
        {[0, 1, 2].map((tab) => (
          <EbSkeleton className="lbv-skel-tab" key={tab} i={tab} />
        ))}
      </div>
      <div className="lbv-actions">
        <EbSkeleton className="lbv-skel-download" />
      </div>
    </div>
    <div className="lbv-body">
      <div className="lbv-skel-grid">
        {/* The BHAG bar spans the full width above the columns, as it does in
            the real grid. */}
        <EbSkeleton className="lbv-skel-bhag" />
        <div className="lbv-skel-cols">
          {[0, 1, 2, 3].map((col) => (
            <div className="lbv-skel-col" key={col}>
              <EbSkeleton className="lbv-skel-col-head" i={col} strong />
              {/* Uneven card counts, on purpose: the real columns hold
                  different numbers of WIGs, and four identical stacks read as a
                  placeholder pattern rather than as content that has not
                  arrived. */}
              {Array.from({ length: [3, 2, 3, 2][col] }).map((_, card) => (
                // eslint-disable-next-line react/no-array-index-key
                <EbSkeleton
                  className="lbv-skel-card"
                  key={card}
                  i={col + card}
                />
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

/**
 * @param blueprint    The locked record — the single source for all three tabs.
 *                     Fetched over REST by each app's wrapper and normalised to
 *                     `{ sections, connectors, roster, bhag, locked_at }`. It
 *                     used to be the live socket snapshot; that was removed
 *                     because the session ends at the terminal lock and this
 *                     screen is opened long after. The SHAPE is unchanged, which
 *                     is why nothing below this line had to move.
 * @param tab          Which tab is showing; one of `LOCKED_TABS`.
 * @param onTab        Called with a tab key when one is clicked.
 * @param CanvasComponent  The connectors canvas. Injected rather than imported
 *                     because it lives outside `_shared/` in both apps and at a
 *                     DIFFERENT relative path in each — importing it directly is
 *                     the one thing that would stop these two copies being
 *                     byte-identical.
 */
// Formatted without a date library on purpose — this file imports nothing
// app-specific so it can stay byte-identical, and `moment` sits at a different
// version in each repo. Returns null on a missing or unparseable value, and the
// tooltip drops the clause rather than printing "Invalid Date".
const formatLockedOn = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(undefined, {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

const LockedBlueprintView = ({
  blueprint,
  tab,
  onTab,
  // ⚠️ `companyName` was removed from this list on 2026-09-03. Its only reader
  // was the client-side PDF, which used it in the header band and the
  // filename; the server names its own file now.
  CanvasComponent,
  // ── The PDF, now the server's job ────────────────────────────────────────
  // ⚠️ PASSED IN, NOT BUILT HERE (2026-09-03). This component used to import
  // `lockedBlueprintPdf.js` and photograph its own DOM with html2canvas + jsPDF.
  // That file is deleted; the backend renders the document.
  //
  // It arrives as a prop rather than being called directly because the URL and
  // the auth differ between the two apps — the coach's is behind `/coach/` with
  // an admin-impersonation header path — and this file is BYTE-IDENTICAL across
  // both repos. Each app's locked screen owns the request; this owns the button.
  onDownload,
  downloading = false,
  downloadError = null,
}) => {
  // ⚠️ NO LONGER A CAPTURE TARGET — the PDF is the server's now. It survives
  // because `applyZoom` reaches through it for the connections scroller, which
  // is the only remaining reader.
  const bodyRef = useRef(null);
  const [zoom, setZoom] = useState(1);

  const lockedOn = formatLockedOn(blueprint?.locked_at);

  // Zoom about the viewport's centre, so whatever is in the middle of the screen
  // stays there. Without this the map appears to lurch toward its top-left on
  // every step, because scaling a `transform-origin: top left` canvas moves
  // every point away from the origin.
  //
  // The scroll write is deliberately OUTSIDE the state updater and reads the
  // previous zoom from a ref: a React updater must be pure, and StrictMode may
  // invoke it twice, which would double-apply the correction. Same shape as the
  // live screen's `applyZoom`, and the same reasoning — see the long note there.
  const zoomRef = useRef(1);
  const applyZoom = (next) => {
    const clamped = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, next));
    const prev = zoomRef.current;
    if (clamped === prev) return;
    zoomRef.current = clamped;
    setZoom(clamped);

    // Scoped to THIS screen's scroller. The canvas renders its own `.wcx-scroll`,
    // and the live screen has one too — an unscoped query would find whichever
    // came first in the document.
    const scroller = bodyRef.current?.querySelector(".wcx-scroll");
    if (!scroller || !prev) return;
    const ratio = clamped / prev;
    const cx = scroller.scrollLeft + scroller.clientWidth / 2;
    const cy = scroller.scrollTop + scroller.clientHeight / 2;
    // A frame later: the canvas wrapper has to grow to its new size before these
    // offsets are reachable, or the browser clamps them to the old scroll range.
    requestAnimationFrame(() => {
      scroller.scrollLeft = cx * ratio - scroller.clientWidth / 2;
      scroller.scrollTop = cy * ratio - scroller.clientHeight / 2;
    });
  };

  const graph = useMemo(
    () => buildConnectorGraph(blueprint?.connectors, blueprint?.sections),
    [blueprint],
  );
  const yearOneGroups = useMemo(
    () => buildYearOneGroups(blueprint),
    [blueprint],
  );
  const roster = useMemo(() => buildRoster(blueprint), [blueprint]);

  const shortlisted = countShortlisted(yearOneGroups);

  return (
    <div className="lbv">
      <div className="lbv-bar">
        {/* ⚠️ No "Back to Dashboard" button, removed on instruction. The design
            has one at the top-left; the ways back are the breadcrumb trail and
            the browser, both of which already work.

            The cell used to be empty, kept only so the tab row stayed centred
            in its three-column grid. The locked badge now occupies it, which
            is a better use of a cell that existed purely as a spacer — and it
            puts the "this is final" signal in the first place the eye lands. */}
        <div className="lbv-leading">
          {/* `ElegantTooltip`, the product's standard tooltip — not a bespoke
              one, and not the coach app's older `StandardTooltip`.

              `title` is deliberately NOT set alongside it: the browser's own
              tooltip would appear next to the styled one, saying the same
              thing a second later. `aria-label` carries the sentence for
              screen readers, which also covers the badge not being focusable.

              `trigger={["hover"]}` rather than the default hover+focus: the
              badge is a static `<span>`, so there is no focus to catch, and
              declaring one we can't receive would be a lie in the props. */}
          <ElegantTooltip
            content={
              lockedOn
                ? `Locked on ${lockedOn}. Every WIG value, owner and connection is final — nothing on this blueprint can be changed.`
                : "Every WIG value, owner and connection is final — nothing on this blueprint can be changed."
            }
            placement="bottom"
            maxWidth={200}
            trigger={["hover"]}
          >
            <span
              className="lbv-locked-badge"
              aria-label={
                lockedOn
                  ? `Blueprint locked on ${lockedOn}. Nothing on it can be changed.`
                  : "Blueprint locked. Nothing on it can be changed."
              }
            >
              <LockGlyph size={11} />
              Blueprint locked
            </span>
          </ElegantTooltip>
        </div>

        <div className="lbv-tabs" role="tablist">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={tab === t.key}
              className={`lbv-tab${tab === t.key ? " is-active" : ""}`}
              onClick={() => onTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="lbv-actions">
          <button
            type="button"
            className="lbv-download"
            onClick={onDownload}
            disabled={downloading || !blueprint || !onDownload}
          >
            <DownloadGlyph />
            {downloading ? "Preparing…" : "Download PDF"}
          </button>
          {/* `role="alert"` so a failure is announced rather than only seen —
              the button returns to its resting state either way, which on its
              own looks like nothing happened. */}
          {downloadError ? (
            <span className="lbv-download-error" role="alert">
              {downloadError}
            </span>
          ) : null}
        </div>
      </div>

      <div className="lbv-body" ref={bodyRef}>
        {tab === LOCKED_TABS.BLUEPRINT && (
          <BlueprintModeGrid
            sections={blueprint?.sections || []}
            bhag={
              blueprint?.bhag || graph.nodes.find((n) => n.id === "bhag")?.label
            }
            /* No `footnote`. It read "This blueprint is locked. Nothing on it
               can be changed." and was removed on instruction (2026-08-31).
               The screen already says so twice over — the breadcrumb reads
               "Locked Blueprint" and every control is gone — so it was a line
               of text restating what the whole screen already communicates. */
          />
        )}

        {tab === LOCKED_TABS.CONNECTIONS && (
          <div className="lbv-connections">
            <div className="lbv-canvas-wrap">
              {/* Zoom only — no fit-to-width and no reset button. The live
                  screen has both because it is a working surface; here the map
                  is the whole point of the tab, so the controls stay to the two
                  that change what you can see. Clicking the percentage returns
                  to 100%, which is the reset without a third control. */}
              {graph.hasGraph && (
                <div className="lbv-zoom">
                  <button
                    type="button"
                    className="lbv-zoom-btn"
                    onClick={() => applyZoom(zoom - ZOOM_STEP)}
                    disabled={zoom <= ZOOM_MIN}
                    aria-label="Zoom out"
                  >
                    −
                  </button>
                  <button
                    type="button"
                    className="lbv-zoom-value"
                    onClick={() => applyZoom(1)}
                    title="Reset to 100%"
                  >
                    {Math.round(zoom * 100)}%
                  </button>
                  <button
                    type="button"
                    className="lbv-zoom-btn"
                    onClick={() => applyZoom(zoom + ZOOM_STEP)}
                    disabled={zoom >= ZOOM_MAX}
                    aria-label="Zoom in"
                  >
                    +
                  </button>
                </div>
              )}

              {graph.hasGraph ? (
                <CanvasComponent
                  nodes={graph.nodes}
                  bands={graph.bands}
                  connections={graph.connections}
                  scale={zoom}
                  readOnly
                />
              ) : (
                <div className="lbv-empty">
                  No connection map was produced for this blueprint.
                </div>
              )}
            </div>

            {/* No "Connection Overview" panel, removed on instruction
                (2026-08-31). It held three stat tiles (Total WIGs, AI
                Suggested, Connections) and the "Blueprint lock" note.

                On a locked record the stats are the least useful thing on the
                tab — the map itself is what someone opens this to read — and
                the lock note restated what the breadcrumb and the absent
                controls already say. Dropping it also gives the graph the full
                width, which matters more here than anywhere else: this is the
                widest layout in the feature and it was losing 320px to a
                sidebar. */}
          </div>
        )}

        {/* ── The Year 1 tab, REBUILT ON THE LIVE SCREEN'S DESIGN ───────────
            2026-09-03, on instruction ("we also need to same design on
            /locked/year-one screen which we are showing"). Everything below the
            header now comes from `YearOnePlanSections`, the same component both
            live screens render — see that file for why it was extracted rather
            than this markup being restyled to match.

            ⚠️ ONLY THE SHORTLIST IS LISTED — unchanged, and confirmed on
            instruction when the redesign landed ("only shortlisted will show on
            locked screen"). `filterRows` narrows the BODY only. A section with
            nothing chosen is skipped whole, so there are no empty cards. See
            `filterRows` in YearOnePlanSections.js for why the summary must not
            be filtered too.

            ⚠️ STRIPPED BACK TWICE MORE (2026-09-03, on instruction). This tab
            now differs from the live screens in three ways, and all three come
            from one observation: THE TAB IS ALREADY THE SHORTLIST, so anything
            that restates that fact is describing the page back to itself.

              1. Every section OPENS EXPANDED. It used to pass
                 `startCollapsed`, justified on "a record is read, not worked
                 through — the summary is the answer". That justification went
                 with the summary (2 below): a shut section whose header is
                 just a name is a labelled lid over the only content here.
              2. NO `GroupSummary` — `showSummary={false}`, where this
                 previously passed `showTotals={false}` and kept the owner
                 faces. With sections open by default, a strip of initials
                 summarising rows visible three pixels below it adds nothing.
                 The header is the section's name and nothing else.
              3. NO PER-ROW "Shortlisted" TAG — `renderControl` returns null.
                 The heading says "Prioritised WIGs for the next 12 months", the count chip
                 beside it says how many, and `filterRows` means every row on
                 screen is one. An identical tag on every line marked nothing.

            The header keeps its own `lbv-year-*` classes rather than borrowing
            `.yop-head`: that one is a `flex-shrink: 0` band inside the live
            screen's bounded-height shell, and this tab scrolls with the page. */}
        {tab === LOCKED_TABS.YEAR_ONE && (
          <div className="lbv-year-one">
            <div className="lbv-year-head">
              <div>
                <h2 className="lbv-year-title">Prioritised WIGs for the next 12 months</h2>
                <p className="lbv-year-sub">
                  The WIGs prioritised for the next 12 months, and who owns each.
                </p>
              </div>
              {/* "shortlisted", not "WIGs selected" — the word the whole
                  feature settled on, and the one the tags and summaries below
                  now use. */}
              <div className="lbv-year-count">
                <span className="lbv-year-count-value">{shortlisted}</span>
                <span className="lbv-year-count-label">prioritised</span>
              </div>
            </div>

            <YearOnePlanSections
              groups={yearOneGroups}
              filterRows={(row) => row.shortlisted}
              // No summary in the header at all — see point 2 in the block
              // comment above. This supersedes the earlier `showTotals={false}`
              // (which dropped the count and dots but kept the owner faces);
              // `showSummary` drops the lot, leaving the section name alone.
              showSummary={false}
              // ⚠️ NOTHING IN THE SHORTLIST CELL — point 3 above. Returning
              // null rather than an empty span so the row's grid has two
              // children, matching the two-track override in
              // LockedBlueprintView.scss. Leave both in step: a third track
              // with nothing in it would push the owner column off the right
              // edge of the card.
              renderControl={() => null}
              renderOwner={(row) => {
                // ── Owners, plural (handoff #25 §4, 2026-09-14) ──
                // A WIG can carry several. This read `row.ownerId` alone and so
                // printed one name for a row that had three — on the permanent
                // record, which is the worst place to under-report who owns
                // what.
                //
                // The singular fields are still the fallback for a payload that
                // predates the change: `owner_user_id` now means "first owner",
                // so an older row degrades to exactly what it used to show.
                const ownerIds =
                  row.ownerIds && row.ownerIds.length
                    ? row.ownerIds
                    : row.ownerId != null
                      ? [row.ownerId]
                      : [];

                const owners = ownerIds
                  .map((id) => {
                    const member = roster.find((m) => m.id === id);
                    if (member) return member.name;
                    const carried = (row.owners || []).find(
                      (o) => o.user_id === id,
                    );
                    return (
                      carried?.name || (id === row.ownerId ? row.ownerName : "")
                    );
                  })
                  .filter(Boolean);

                // ⚠️ THIS PRINTED EVERY NAME IN FULL AND THAT WAS WRONG.
                // The argument was that a locked record is a document and can
                // take the height. It cannot: the owner cell is one fixed grid
                // column, and six owners turned a row into a 900px tower of
                // one-letter-per-line text. A design that only works at three
                // owners is not a design for a field that accepts ten.
                //
                // So the names are bounded — two lines, then ellipsis — and the
                // avatars cap at four with a "+N". The full list is still
                // reachable, on the cell's `title`, which is the right home for
                // a detail that matters occasionally and must never dictate the
                // row's height.
                const name = owners.join(", ");
                const shownAvatars = owners.slice(0, LOCKED_AVATAR_LIMIT);
                const overflow = owners.length - shownAvatars.length;
                return (
                  // Owner column, and both halves of it are deliberate
                  // (2026-08-31, on instruction):
                  //
                  // ASSIGNED → the person's name, with an avatar showing their
                  // real initials. `initialsFor` derives those from the name it
                  // is given, so it only ever falls back to "?" when the name is
                  // empty — and an empty name can't reach this branch, because
                  // `name` being truthy is what selects it. The old `|| "?"` here
                  // was therefore dead code that could still fire if `roster`
                  // hadn't resolved: it printed a "?" pill next to a name we were
                  // displaying perfectly well. Derived from `name` directly
                  // instead, so the pill can never disagree with the text beside
                  // it.
                  //
                  // UNASSIGNED → a plain "—" and nothing else. No pill, no
                  // "Unassigned" label. On a locked, read-only record an empty
                  // owner is a fact, not a prompt, and the pill was drawing the
                  // eye to the rows with the least to say.
                  <span
                    className={`lbv-year-owner${name ? "" : " is-empty"}`}
                    title={owners.length > 1 ? name : undefined}
                  >
                    {owners.length ? (
                      <>
                        {/* One avatar per owner, stacked — the same mark the
                            live screen uses, so the locked record and the
                            screen it was locked from show the same thing. Each
                            pill's initials come from ITS OWN name, never from
                            the joined string. */}
                        <span className="lbv-year-avatars">
                          {shownAvatars.map((owner) => (
                            <span className="lbv-year-avatar" key={owner}>
                              {initialsFor(owner)}
                            </span>
                          ))}
                          {overflow > 0 ? (
                            <span className="lbv-year-avatar is-more">
                              +{overflow}
                            </span>
                          ) : null}
                        </span>
                        <span className="lbv-year-owner-names">{name}</span>
                      </>
                    ) : (
                      "—"
                    )}
                  </span>
                );
              }}
            />

            {/* ⚠️ Keyed on the SHORTLIST being empty, not on there being no
                groups — those are different states now that every WIG is
                listed. A blueprint with twelve WIGs and none chosen still draws
                twelve rows, four "None shortlisted" summaries, and this line
                explaining that the plan itself is empty. */}
            {shortlisted === 0 && (
              <div className="lbv-empty">
                No WIGs were prioritised for the next 12 months.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default LockedBlueprintView;
